import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import {
  AuthProvider,
  ProtectedRoute,
  LoginPage,
  useAuth,
} from "./components/Auth";
import Navigation from "./components/Navigation";
import EmployeeManagement from "./components/EmployeeManagement";
import ProjectManagement from "./components/ProjectManagement";
import ProjectDetail from "./components/ProjectDetail";
import ProjectKickoffPage from "./components/ProjectKickoffPage";
import { SlotDetail, SlotsManagement } from "./components/Slot";
import AddressManagement from "./components/AddressManagement";
import ClientManagement from "./components/ClientManagement";
import HoursManagement from "./components/HoursManagement";
import ObjectiveBoard from "./components/ObjectiveBoard";
import Dashboard from "./components/Dashboard";
import ChatBot from "./components/Chat";
import ExpenseManagement from "./components/ExpenseManagement";
import BillingBatchManagement from "./components/BillingBatchManagement";
import BacklogPage from "./components/BacklogPage";
import { NexusSyncProvider } from "./context/NexusSyncContext";
import UserAiSettings from "./components/UserPreferences/UserAiSettings";
import HolacracyPage from "./components/HolacracyPage";
import InventoryManagement from "./components/InventoryManagement";
import PilotagePage from "./components/PilotagePage";
import PresenceHeartbeat from "./components/PresenceHeartbeat";
import MeetingContextBanner from "./components/Holacracy/MeetingContextBanner";
import IterationEndNotificationPage from "./components/IterationEndNotificationPage";
import ToolsPage from "./components/ToolsPage";
import GuttenbergHome from "./components/Guttenberg/GuttenbergHome";
import GuttenbergReportPage from "./components/Guttenberg/GuttenbergReportPage";
import KnowledgePage from "./components/Knowledge";

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const isSuperAdmin = Number(user?.employee_id ?? user?.id) === 1;
  if (!user?.is_admin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <NexusSyncProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <div className="min-h-screen bg-gray-100">
                    <PresenceHeartbeat />
                    <Navigation />
                    <MeetingContextBanner />
                    <main className="py-1">
                      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                        <Routes>
                          <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
                          <Route path="/employees" element={<AdminRoute><EmployeeManagement /></AdminRoute>} />
                          <Route path="/projects" element={<ProjectManagement />} />
                          <Route path="/project/:projectId" element={<ProjectDetail />} />
                          <Route path="/project/:projectId/kickoff" element={<ProjectKickoffPage />} />
                          <Route path="/slots" element={<SlotsManagement displayMode="timeline" />} />
                          <Route path="/slots/current" element={<SlotsManagement displayMode="current" />} />
                          <Route path="/slot/:slotId" element={<SlotDetail />} />
                          <Route
                            path="/hours"
                            element={
                              <div className="max-w-6xl mx-auto">
                                <HoursManagement />
                              </div>
                            }
                          />
                          <Route path="/clients" element={<ClientManagement />} />
                          <Route path="/addresses" element={<AddressManagement />} />
                          <Route path="/objectives" element={<ObjectiveBoard />} />
                          <Route path="/" element={<ProjectManagement />} />
                          <Route path="/backlog" element={<BacklogPage />} />
                          <Route path="/expenses" element={<ExpenseManagement />} />
                          <Route path="/billing-batches" element={<BillingBatchManagement />} />
                          <Route path="/iteration-end-notifications/:eventId" element={<IterationEndNotificationPage />} />
                          <Route path="/holacracy" element={<HolacracyPage />} />
                          <Route path="/ai-preferences" element={<UserAiSettings />} />
                          <Route path="/inventory" element={<InventoryManagement />} />
                          <Route path="/pilotage" element={<AdminRoute><PilotagePage /></AdminRoute>} />
                          <Route path="/tools" element={<ToolsPage />} />
                          <Route path="/tools/knowledge" element={<KnowledgePage />} />
                          <Route path="/tools/guttenberg" element={<GuttenbergHome />} />
                          <Route path="/tools/guttenberg/reports/:projectId" element={<GuttenbergReportPage />} />
                        </Routes>
                      </div>
                    </main>
                    {/* Chatbot */}
                    <ChatBot />
                    <footer className="bg-white border-t border-gray-200">
                      <div className="max-w-full mx-auto py-6 px-4 sm:px-6 lg:px-8">
                        <p className="text-center text-sm text-gray-500">
                          © {new Date().getFullYear()} Innowide DeepFlow. Tous droits réservés.
                        </p>
                      </div>
                    </footer>
                  </div>
                </ProtectedRoute>
              }
            />
          </Routes>
        </NexusSyncProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
